import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { formatBRL, formatarData, diaDaSemana, nomeMes } from "@/lib/utils";
import { ModalRestricoes } from "@/components/ModalRestricoes";

export function MeusTurnos() {
  const { user } = useAuth();
  const { isAdmin, podeGerirEscala } = usePermissions();
  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getUTCFullYear());
  const [mes, setMes] = useState(hoje.getUTCMonth() + 1);
  const [showRestricoes, setShowRestricoes] = useState(false);

  const profissionaisQ = trpc.profissionais.listar.useQuery({ incluirInativos: false });
  const meuProf = useMemo(() => {
    return (profissionaisQ.data ?? []).find((p) => p.userId === user?.id) ?? null;
  }, [profissionaisQ.data, user]);

  const [profissionalIdSel, setProfissionalIdSel] = useState<number | null>(null);
  const profissionalId = profissionalIdSel ?? meuProf?.id ?? null;
  const nomeSelecionado =
    (profissionaisQ.data ?? []).find((p) => p.id === profissionalId)?.nome ?? null;

  // 24/09: a coordenação de escala escolhe qualquer pessoa para cuidar das
  // restrições de agenda dela, mas turnos com valor e remuneração de OUTRA
  // pessoa continuam só do admin (o backend também barra: remuneracao.ts).
  const podeVerValores = !!profissionalId && (isAdmin || profissionalId === meuProf?.id);

  const detalhesQ = trpc.remuneracao.detalhesProfissionalMes.useQuery(
    profissionalId ? { profissionalId, ano, mes } : (undefined as never),
    { enabled: podeVerValores },
  );

  function navegar(delta: number) {
    let novo = mes + delta;
    let novoAno = ano;
    if (novo < 1) { novo = 12; novoAno--; }
    if (novo > 12) { novo = 1; novoAno++; }
    setMes(novo);
    setAno(novoAno);
  }

  const totalTurnos = detalhesQ.data?.turnos.length ?? 0;
  const dias = new Set((detalhesQ.data?.turnos ?? []).map((t) => t.data)).size;
  const total = (detalhesQ.data?.turnos ?? []).reduce((s, t) => s + Number(t.valor), 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-slate-900">
          Turnos — {detalhesQ.data?.profissional?.nome ?? nomeSelecionado ?? meuProf?.nome ?? "..."}
        </h1>
        <div className="flex items-center gap-2">
          {podeGerirEscala && (
            <Select
              value={profissionalId ? String(profissionalId) : ""}
              onValueChange={(v) => setProfissionalIdSel(Number(v))}
            >
              <SelectTrigger className="w-56"><SelectValue placeholder="Profissional" /></SelectTrigger>
              <SelectContent>
                {(profissionaisQ.data ?? []).map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>{p.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {profissionalId && (
            <Button variant="outline" onClick={() => setShowRestricoes(true)}>
              Restrições
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-center gap-2">
        <Button variant="outline" size="icon" onClick={() => navegar(-1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-44 text-center text-lg font-semibold text-slate-900">
          {nomeMes(mes)} {ano}
        </div>
        <Button variant="outline" size="icon" onClick={() => navegar(1)}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {profissionalId && !podeVerValores ? (
        <Card>
          <CardContent className="p-5 text-sm text-slate-600">
            Turnos com valor e remuneração de outra pessoa ficam com a administração do RH.
            Aqui você cuida das restrições de agenda de {nomeSelecionado ?? "quem foi escolhido"} —
            botão <span className="font-medium">Restrições</span>, acima. A escala do mês está em
            Escalas.
          </CardContent>
        </Card>
      ) : (
      <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat label="Total de Turnos" value={totalTurnos} />
        <Stat label="Dias Trabalhados" value={dias} />
        <Stat label="Remuneração Total" value={formatBRL(total)} highlight />
      </div>

      <Card>
        <CardHeader><CardTitle>Turnos do mês</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Dia</TableHead>
                <TableHead>Turno</TableHead>
                <TableHead>Horário</TableHead>
                <TableHead className="text-right">Valor</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(detalhesQ.data?.turnos ?? []).map((t) => (
                <TableRow key={t.turnoId}>
                  <TableCell>{formatarData(t.data)}</TableCell>
                  <TableCell>{diaDaSemana(t.data)}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full" style={{ backgroundColor: t.tipoTurnoCor }} />
                      {t.tipoTurnoNome}
                    </span>
                  </TableCell>
                  <TableCell>{t.horaInicio} – {t.horaFim}</TableCell>
                  <TableCell className="text-right font-semibold text-brand-600">{formatBRL(t.valor)}</TableCell>
                </TableRow>
              ))}
              {!detalhesQ.data?.turnos.length && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-slate-400">Nenhum turno neste mês.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      </>
      )}

      {profissionalId && showRestricoes && (
        <ModalRestricoes
          profissionalId={profissionalId}
          profissionalNome={detalhesQ.data?.profissional?.nome ?? nomeSelecionado ?? ""}
          open={showRestricoes}
          onOpenChange={setShowRestricoes}
        />
      )}
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string | number; highlight?: boolean }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
        <div className={`mt-1 text-2xl font-bold ${highlight ? "text-brand-600" : "text-slate-900"}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
